import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, downloadFile } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useHrMutation, useRuleSets, useRun, useRuns, type RuleSet, type RunLine } from '../api/hr';
import { hours, useApprovedOvertime } from '../api/attendance';
import { addDays, formatDate, formatGhs, parseCedis, type Tone } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Icon, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';

const STATUS: Record<string, { label: string; tone: Tone }> = {
  CALCULATED: { label: 'Waiting for approval', tone: 'warn' },
  APPROVED: { label: 'Approved, not yet paid', tone: 'info' },
  PAID: { label: 'Paid', tone: 'good' },
};
const monthName = (p: string) => new Date(`${p}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const pct = (bps: number) => `${(bps / 100).toString()}%`;

type Tab = 'runs' | 'rules';

export function Payroll() {
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>('runs');
  return (
    <div className="page">
      <PageHead eyebrow="Payroll" title="Pay staff" />
      <div className="seg" role="group" aria-label="Show">
        <button type="button" aria-pressed={tab === 'runs'} onClick={() => setTab('runs')}>
          Monthly payroll
        </button>
        <button type="button" aria-pressed={tab === 'rules'} onClick={() => setTab('rules')}>
          Pay rules
        </button>
      </div>
      {tab === 'runs' ? <RunsTab canRun={can('payroll.run')} /> : <RulesTab />}
    </div>
  );
}

/* -------------------------------------------------------------------- runs */
function RunsTab({ canRun }: { canRun: boolean }) {
  const { me } = useAuth();
  const runs = useRuns();
  const nav = useNavigate();
  const toast = useToast();
  const report = useReport();
  const [creating, setCreating] = useState(false);
  const thisMonth = (me?.property.today ?? '').slice(0, 7);
  const [period, setPeriod] = useState(addDays(`${thisMonth}-01`, -1).slice(0, 7));
  const make = useHrMutation((p: string) => api<{ id: string }>('/payroll/runs', { method: 'POST', body: { period: p } }));

  return (
    <>
      {canRun ? (
        <div>
          <Button variant="primary" onClick={() => setCreating(true)}>
            Work out a month’s pay
          </Button>
        </div>
      ) : null}
      {runs.isLoading ? (
        <Loading rows={2} />
      ) : runs.error ? (
        <ErrorNote error={runs.error} retry={() => void runs.refetch()} />
      ) : (runs.data ?? []).length === 0 ? (
        <div className="card flat">
          <Empty icon="books" title="No payroll yet">First add staff and their pay (People), and have pay rules entered and approved (Pay rules). Then work out a month here.</Empty>
        </div>
      ) : (
        <div className="list">
          {runs.data!.map((r) => (
            <Link key={r.id} to={`/payroll/${r.id}`} className="item">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">{monthName(r.period)}</span>
                <Badge tone={STATUS[r.status]!.tone}>{STATUS[r.status]!.label}</Badge>
              </div>
              <span className="small muted">
                {r.totals.headcount} {r.totals.headcount === 1 ? 'person' : 'people'} · gross {formatGhs(r.totals.gross)} · take-home <strong className="num">{formatGhs(r.totals.net)}</strong>
              </span>
            </Link>
          ))}
        </div>
      )}
      <Modal open={creating} onClose={() => setCreating(false)} title="Work out a month’s pay">
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            make.mutate(period, { onError: report, onSuccess: (r) => { toast.ok('Pay worked out. Check it, then ask the owner to approve.'); setCreating(false); nav(`/payroll/${(r as { id: string }).id}`); } });
          }}
        >
          <Field id="np-month" label="Month" hint="Uses everyone’s pay, approved leave and advances as they are now">
            <input id="np-month" className="input" type="month" value={period} max={thisMonth} onChange={(e) => setPeriod(e.target.value)} />
          </Field>
          <div className="modal-foot">
            <Button type="button" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={!period} loading={make.isPending}>
              Work it out
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}

/* ------------------------------------------------------------------- rules */
function RulesTab() {
  const { can } = useAuth();
  const sets = useRuleSets();
  const toast = useToast();
  const report = useReport();
  const [adding, setAdding] = useState(false);
  const approve = useHrMutation((id: string) => api(`/payroll/rules/${id}/approve`, { method: 'POST', body: {} }));
  const drop = useHrMutation((id: string) => api(`/payroll/rules/${id}`, { method: 'DELETE' }));

  return (
    <>
      <div className="banner info">
        Tax bands and pension rates are entered here by the accountant and approved by the owner. They are never built into the software, because they change. Check them against the current rules before approving.
      </div>
      {can('payroll.rules') ? (
        <div>
          <Button variant="primary" onClick={() => setAdding(true)}>
            Add pay rules
          </Button>
        </div>
      ) : null}
      {sets.isLoading ? (
        <Loading rows={2} />
      ) : sets.error ? (
        <ErrorNote error={sets.error} retry={() => void sets.refetch()} />
      ) : (sets.data ?? []).length === 0 ? (
        <div className="card flat">
          <Empty icon="inbox" title="No pay rules yet">Payroll cannot be worked out until rules are approved.</Empty>
        </div>
      ) : (
        <div className="stack">
          {sets.data!.map((r) => (
            <section key={r.id} className="card">
              <div className="card-head">
                <h2 className="h2">{r.name}</h2>
                <Badge tone={r.approved ? 'good' : 'warn'}>{r.approved ? 'Approved' : 'Waiting for approval'}</Badge>
              </div>
              <RuleSummary r={r} />
              <div className="row" style={{ marginTop: 10 }}>
                {!r.approved && can('payroll.approve') ? (
                  <Button variant="primary" loading={approve.isPending} onClick={() => approve.mutate(r.id, { onError: report, onSuccess: () => toast.ok('Rules approved') })}>
                    Approve these rules
                  </Button>
                ) : null}
                {!r.approved && can('payroll.rules') ? (
                  <Button variant="ghost" loading={drop.isPending} onClick={() => drop.mutate(r.id, { onError: report, onSuccess: () => toast.ok('Removed') })}>
                    Remove
                  </Button>
                ) : null}
              </div>
            </section>
          ))}
        </div>
      )}
      {adding ? <RulesForm onClose={() => setAdding(false)} /> : null}
    </>
  );
}

function RuleSummary({ r }: { r: RuleSet }) {
  const c = r.config;
  let lower = 0;
  return (
    <dl className="kv">
      <dt>Takes effect</dt>
      <dd>{formatDate(r.effectiveFrom, true)}</dd>
      <dt>Employee pension</dt>
      <dd>{pct(c.pension.employeeBps)} of {c.pension.base === 'BASIC' ? 'basic pay' : 'gross pay'}</dd>
      <dt>Employer pension</dt>
      <dd>{pct(c.pension.employerBps)}</dd>
      <dt>Pension counts against tax</dt>
      <dd>{c.pensionDeductibleForTax ? 'Yes' : 'No'}</dd>
      <dt>Unpaid day</dt>
      <dd>1 / {c.unpaidDayDivisor} of basic</dd>
      <dt>Income tax (monthly)</dt>
      <dd>
        {c.taxBands.map((b, i) => {
          const from = lower;
          if (b.upTo !== null) lower = b.upTo;
          return (
            <div key={i}>
              {b.upTo === null ? `Above ${formatGhs(from)}` : `${formatGhs(from)} to ${formatGhs(b.upTo)}`}: {pct(b.rateBps)}
            </div>
          );
        })}
      </dd>
    </dl>
  );
}

const bpsOf = (text: string): number | null => {
  const n = Number(text.trim());
  return text.trim() !== '' && Number.isFinite(n) && n >= 0 && n <= 100 ? Math.round(n * 100) : null;
};

function RulesForm({ onClose }: { onClose: () => void }) {
  const { me } = useAuth();
  const toast = useToast();
  const report = useReport();
  const [name, setName] = useState('');
  const [from, setFrom] = useState(me?.property.today ?? '');
  const [emp, setEmp] = useState('');
  const [er, setEr] = useState('');
  const [base, setBase] = useState<'BASIC' | 'GROSS'>('BASIC');
  const [deductible, setDeductible] = useState(true);
  const [divisor, setDivisor] = useState('30');
  const [bands, setBands] = useState<{ upTo: string; rate: string }[]>([{ upTo: '', rate: '0' }, { upTo: '', rate: '' }]);
  const make = useHrMutation((body: Record<string, unknown>) => api('/payroll/rules', { method: 'POST', body }));

  const parsed = bands.map((b, i) => ({ upTo: i === bands.length - 1 ? null : parseCedis(b.upTo), rate: bpsOf(b.rate) }));
  const rising = parsed.every((b, i) => i === 0 || b.upTo === null || parsed[i - 1]!.upTo === null || (b.upTo as number) > (parsed[i - 1]!.upTo as number));
  const bandsOk = parsed.every((b, i) => b.rate !== null && (i === parsed.length - 1 || (b.upTo !== null && b.upTo > 0))) && rising;
  const valid = name.trim().length >= 3 && !!from && bpsOf(emp) !== null && bpsOf(er) !== null && bandsOk && Number(divisor) >= 20 && Number(divisor) <= 31;

  return (
    <Modal open onClose={onClose} title="Add pay rules">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!valid) return;
          make.mutate(
            {
              name: name.trim(),
              effectiveFrom: from,
              config: {
                pension: { employeeBps: bpsOf(emp), employerBps: bpsOf(er), base },
                pensionDeductibleForTax: deductible,
                unpaidDayDivisor: Number(divisor),
                taxBands: parsed.map((b) => ({ upTo: b.upTo, rateBps: b.rate })),
              },
            },
            { onError: report, onSuccess: () => { toast.ok('Saved. The owner must approve these before payroll can use them.'); onClose(); } },
          );
        }}
      >
        <Field id="pr-name" label="Name" hint="e.g. Rules from January 2027">
          <input id="pr-name" className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field id="pr-from" label="Takes effect on" hint="Used for every month from this date until newer approved rules start">
          <input id="pr-from" className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <div className="eyebrow">Pension</div>
        <div className="grid-2">
          <Field id="pr-emp" label="Employee pays (%)">
            <input id="pr-emp" className="input" inputMode="decimal" value={emp} onChange={(e) => setEmp(e.target.value)} />
          </Field>
          <Field id="pr-er" label="Employer pays (%)">
            <input id="pr-er" className="input" inputMode="decimal" value={er} onChange={(e) => setEr(e.target.value)} />
          </Field>
        </div>
        <div className="seg" role="group" aria-label="Pension is worked out on">
          <button type="button" aria-pressed={base === 'BASIC'} onClick={() => setBase('BASIC')}>
            On basic pay
          </button>
          <button type="button" aria-pressed={base === 'GROSS'} onClick={() => setBase('GROSS')}>
            On gross pay
          </button>
        </div>
        <label className="row small">
          <input type="checkbox" checked={deductible} onChange={(e) => setDeductible(e.target.checked)} /> The employee’s pension is taken off before income tax
        </label>
        <div className="eyebrow">Income tax bands (a month)</div>
        <p className="small muted">Each band covers income up to its limit; the last band has no limit.</p>
        {bands.map((b, i) => {
          const last = i === bands.length - 1;
          return (
            <div key={i} className="grid-2" style={{ alignItems: 'end' }}>
              {last ? (
                <Field id={`bd-up-${i}`} label="Above the last limit">
                  <input id={`bd-up-${i}`} className="input" value="No limit" disabled />
                </Field>
              ) : (
                <Field id={`bd-up-${i}`} label="Up to (GH₵)">
                  <input id={`bd-up-${i}`} className="input" inputMode="decimal" value={b.upTo} onChange={(e) => setBands(bands.map((x, j) => (j === i ? { ...x, upTo: e.target.value } : x)))} />
                </Field>
              )}
              <div className="row">
                <Field id={`bd-rt-${i}`} label="Tax rate (%)">
                  <input id={`bd-rt-${i}`} className="input" inputMode="decimal" value={b.rate} onChange={(e) => setBands(bands.map((x, j) => (j === i ? { ...x, rate: e.target.value } : x)))} />
                </Field>
                {bands.length > 2 && !last ? (
                  <Button type="button" variant="ghost" aria-label="Remove band" onClick={() => setBands(bands.filter((_, j) => j !== i))}>
                    ✕
                  </Button>
                ) : null}
              </div>
            </div>
          );
        })}
        {!rising ? <div className="banner bad">Each band’s limit must be higher than the one before.</div> : null}
        {bands.length < 12 ? (
          <div>
            <Button type="button" onClick={() => setBands([...bands.slice(0, -1), { upTo: '', rate: '' }, bands[bands.length - 1]!])}>
              Add a band
            </Button>
          </div>
        ) : null}
        <Field id="pr-div" label="Days in a month for unpaid leave" hint="One unpaid day takes off 1 / this many of basic pay">
          <input id="pr-div" className="input" inputMode="numeric" value={divisor} onChange={(e) => setDivisor(e.target.value.replace(/\D/g, ''))} />
        </Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={make.isPending}>
            Save rules
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* --------------------------------------------------------------- run detail */
export function PayrollRun() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const nav = useNavigate();
  const run = useRun(id);
  const toast = useToast();
  const report = useReport();
  const [adjusting, setAdjusting] = useState<RunLine | null>(null);
  const [paying, setPaying] = useState(false);
  const act = useHrMutation((v: { path: string; method?: 'POST' | 'DELETE'; body?: Record<string, unknown> }) => api(`/payroll/runs/${id}${v.path}`, { method: v.method ?? 'POST', body: v.body ?? {} }));
  const dl = (path: string, name: string) => void downloadFile(`/payroll/runs/${id}/${path}`, name).catch(report);

  if (run.isLoading) return <div className="page"><Loading /></div>;
  if (run.error || !run.data) return <div className="page"><ErrorNote error={run.error} retry={() => void run.refetch()} /></div>;
  const r = run.data;
  const open = r.status === 'CALCULATED';
  const t = r.totals;
  const warnings = r.lines.filter((l) => l.warnings.length);

  return (
    <div className="page">
      <Link to="/payroll" className="row small" style={{ textDecoration: 'none' }}>
        <span style={{ width: 18, height: 18 }}>
          <Icon name="back" />
        </span>
        All payroll
      </Link>
      <div className="page-head">
        <div>
          <div className="eyebrow">{r.ruleSet ?? 'Payroll'}</div>
          <h1 className="h1">{monthName(r.period)}</h1>
          <Badge tone={STATUS[r.status]!.tone}>{STATUS[r.status]!.label}</Badge>
        </div>
      </div>

      <section className="stats" aria-label="Totals">
        <div className="card stat"><span className="eyebrow">Gross pay</span><span className="figure num">{formatGhs(t.gross)}</span></div>
        <div className="card stat"><span className="eyebrow">Income tax</span><span className="figure num">{formatGhs(t.paye)}</span></div>
        <div className="card stat"><span className="eyebrow">Pension (both shares)</span><span className="figure num">{formatGhs(t.pensionEmployee + t.pensionEmployer)}</span></div>
        <div className="card stat"><span className="eyebrow">Take-home</span><span className="figure num">{formatGhs(t.net)}</span></div>
      </section>
      <p className="small muted">The hotel’s total cost this month is {formatGhs(t.employerCost)} (gross pay plus the employer’s pension share).</p>

      {warnings.length ? (
        <div className="banner">
          {warnings.map((l) => (
            <div key={l.employeeId}>{l.employeeName}: {l.warnings.join(' ')}</div>
          ))}
        </div>
      ) : null}

      <div className="table-scroll">
        <table className="ledger fin">
          <thead>
            <tr>
              <th>Person</th>
              <th className="r">Gross</th>
              <th className="r">Pension</th>
              <th className="r">Tax</th>
              <th className="r">Other</th>
              <th className="r">Take-home</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {r.lines.map((l) => (
              <tr key={l.employeeId}>
                <td>
                  {l.employeeName}
                  <div className="hint">{l.position}{l.unpaidDays ? ` · ${l.unpaidDays} unpaid day${l.unpaidDays === 1 ? '' : 's'}` : ''}{l.extraEarnings ? ` · extra ${formatGhs(l.extraEarnings)} (${l.extraNote})` : ''}</div>
                </td>
                <td className="r num">{formatGhs(l.gross)}</td>
                <td className="r num">{formatGhs(l.pensionEmployee)}</td>
                <td className="r num">{formatGhs(l.paye)}</td>
                <td className="r num">{formatGhs(l.loanRepayment + l.otherDeductions)}</td>
                <td className="r num"><strong>{formatGhs(l.net)}</strong></td>
                <td className="r">
                  <Link to={`/payroll/${id}/payslip/${l.employeeId}`} className="linklike">Payslip</Link>
                  {open && can('payroll.run') ? (
                    <>
                      {' · '}
                      <button type="button" className="linklike" onClick={() => setAdjusting(l)}>Adjust</button>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
            <tr className="strong">
              <td>Total · {t.headcount} {t.headcount === 1 ? 'person' : 'people'}</td>
              <td className="r num">{formatGhs(t.gross)}</td>
              <td className="r num">{formatGhs(t.pensionEmployee)}</td>
              <td className="r num">{formatGhs(t.paye)}</td>
              <td className="r num">{formatGhs(t.loanRepayment + t.otherDeductions)}</td>
              <td className="r num">{formatGhs(t.net)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>

      <div className="stack">
        {open ? (
          <>
            {can('payroll.run') ? (
              <div className="row">
                <Button loading={act.isPending} onClick={() => act.mutate({ path: '/recalculate' }, { onError: report, onSuccess: () => toast.ok('Worked out again from today’s records') })}>
                  Work out again
                </Button>
                <Button variant="ghost" loading={act.isPending} onClick={() => act.mutate({ path: '', method: 'DELETE' }, { onError: report, onSuccess: () => { toast.ok('Discarded'); nav('/payroll'); } })}>
                  Discard
                </Button>
              </div>
            ) : null}
            {can('payroll.approve') ? (
              <Button variant="primary" block loading={act.isPending} onClick={() => act.mutate({ path: '/approve' }, { onError: report, onSuccess: () => toast.ok('Approved and recorded in the books') })}>
                Approve this payroll
              </Button>
            ) : (
              <p className="hint">The owner approves payroll, and cannot approve a payroll they worked out themselves.</p>
            )}
          </>
        ) : null}
        {r.status === 'APPROVED' && can('payroll.approve') ? (
          <Button variant="primary" block onClick={() => setPaying(true)}>
            Record that staff were paid
          </Button>
        ) : null}
        <div className="row">
          <Button onClick={() => dl('register', `payroll-register-${r.period}.csv`)}>Download register</Button>
          {!open && can('hr.pay_data') ? <Button onClick={() => dl('payment-file', `payment-file-${r.period}.csv`)}>Payment file</Button> : null}
          {!open && can('hr.pay_data') ? <Button onClick={() => dl('remittance?format=csv', `remittance-${r.period}.csv`)}>Tax and pension</Button> : null}
        </div>
      </div>

      {adjusting ? <AdjustDialog line={adjusting} runId={id} onClose={() => setAdjusting(null)} /> : null}
      {paying ? <PayDialog runId={id} total={t.net} onClose={() => setPaying(false)} /> : null}
    </div>
  );
}

function AdjustDialog({ line, runId, onClose }: { line: RunLine; runId: string; onClose: () => void }) {
  const run = useRun(runId);
  const overtime = useApprovedOvertime(run.data?.period);
  const approvedMinutes = overtime.data?.find((x) => x.employeeId === line.employeeId)?.approvedMinutes ?? 0;
  const toast = useToast();
  const report = useReport();
  const [extra, setExtra] = useState(line.extraEarnings ? (line.extraEarnings / 100).toFixed(2) : '');
  const [extraNote, setExtraNote] = useState(line.extraNote ?? '');
  const [other, setOther] = useState(line.otherDeductions ? (line.otherDeductions / 100).toFixed(2) : '');
  const [otherNote, setOtherNote] = useState(line.otherNote ?? '');
  const save = useHrMutation((body: Record<string, unknown>) => api(`/payroll/runs/${runId}/lines/${line.employeeId}`, { method: 'PATCH', body }));
  const e = extra.trim() === '' ? 0 : parseCedis(extra);
  const o = other.trim() === '' ? 0 : parseCedis(other);
  const valid = e !== null && o !== null && (e === 0 || extraNote.trim().length >= 2) && (o === 0 || otherNote.trim().length >= 2);
  return (
    <Modal open onClose={onClose} title={`Adjust ${line.employeeName}`}>
      <form
        className="stack"
        onSubmit={(ev: FormEvent) => {
          ev.preventDefault();
          if (valid) save.mutate({ extraEarnings: e, extraNote: e ? extraNote.trim() : null, otherDeductions: o, otherNote: o ? otherNote.trim() : null }, { onError: report, onSuccess: () => { toast.ok('Updated'); onClose(); } });
        }}
      >
        <div className="eyebrow">Extra pay (taxable)</div>
        {approvedMinutes > 0 ? <p className="hint">Overtime a manager approved for this month: {hours(approvedMinutes)}. The amount to pay is for you to decide.</p> : null}
        <div className="grid-2">
          <Field id="aj-extra" label="Amount (GH₵)"><input id="aj-extra" className="input" inputMode="decimal" value={extra} onChange={(x) => setExtra(x.target.value)} placeholder="0.00" /></Field>
          <Field id="aj-en" label="What for"><input id="aj-en" className="input" value={extraNote} onChange={(x) => setExtraNote(x.target.value)} placeholder="Overtime, bonus…" /></Field>
        </div>
        <div className="eyebrow">Other deduction</div>
        <div className="grid-2">
          <Field id="aj-other" label="Amount (GH₵)"><input id="aj-other" className="input" inputMode="decimal" value={other} onChange={(x) => setOther(x.target.value)} placeholder="0.00" /></Field>
          <Field id="aj-on" label="What for"><input id="aj-on" className="input" value={otherNote} onChange={(x) => setOtherNote(x.target.value)} placeholder="Uniform, shortage…" /></Field>
        </div>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}

function PayDialog({ runId, total, onClose }: { runId: string; total: number; onClose: () => void }) {
  const toast = useToast();
  const report = useReport();
  const [method, setMethod] = useState<'BANK' | 'MOMO' | 'CASH'>('BANK');
  const pay = useHrMutation((m: string) => api(`/payroll/runs/${runId}/pay`, { method: 'POST', body: { method: m } }));
  return (
    <Modal open onClose={onClose} title="Staff were paid">
      <div className="stack">
        <div className="total-row"><span>Total paid out</span><span className="amount num">{formatGhs(total)}</span></div>
        <div className="seg" role="group" aria-label="Paid from">
          {(['BANK', 'MOMO', 'CASH'] as const).map((m) => (
            <button key={m} type="button" aria-pressed={method === m} onClick={() => setMethod(m)}>
              {m === 'BANK' ? 'From the bank' : m === 'MOMO' ? 'Mobile money' : 'From cash'}
            </button>
          ))}
        </div>
        <p className="hint">Do this after the money has actually gone out. It moves what the hotel owes staff out of the books.</p>
        <div className="modal-foot">
          <Button onClick={onClose}>Not yet</Button>
          <Button variant="primary" loading={pay.isPending} onClick={() => pay.mutate(method, { onError: report, onSuccess: () => { toast.ok('Recorded as paid'); onClose(); } })}>
            Record payment
          </Button>
        </div>
      </div>
    </Modal>
  );
}
